import { ref, computed, watch, onMounted, nextTick, toValue } from 'vue'
import { type MaybeRefOrGetter, type Ref } from 'vue'

import EventBus from '../event-bus/EventBus'
import { useAdaStore } from '../stores/ada'
import { usePageStore } from '../stores/page'
import { useConfigStore } from '../stores/config'
import { useSourcepointStore } from '../stores/sourcepoint'

import type { ComposedMetaData } from '@xcel-next/composer-schema'
import type { DynamicAdSlots, DynamicAdClientConfig, DynamicAdArticleClientConfig } from '@xcel-next/rendering-tools'
import type { BxMbWindow } from '../types'

export type ScreenType = 'largeDesktop' | 'smallDesktop' | 'mobile'

type ClientConfig = {
  vw: number,
  vh: number,
  screenType: ScreenType,
  startOffsetTop: number,
  slotDistanceOffset: number,
  slotList: string[],
  article: { leadOffset?: number, asideWrapperHeight?: number },
  articleLeadOffset?: number,
  exclusionList: string[],
  latestAllocatedDynamicAdSlots: string[],
  latestRegisteredDynamicAdSlots: string[]
}

const screenTypeMinWidth: Record<ScreenType, number> = {
  largeDesktop: 1000,
  smallDesktop: 768,
  mobile: 0
}

export function useAdaDynamicSlotLoader (
  metaData: MaybeRefOrGetter<ComposedMetaData | undefined>,
  taboolaEnabled: MaybeRefOrGetter<boolean>,
  enableNcbInDynamicMode: MaybeRefOrGetter<boolean>,
  registerSlots: Ref<string[]>,
  initMoltenBundle: () => void
) {
  const adaStore = useAdaStore()
  const pageStore = usePageStore()
  const configStore = useConfigStore()
  const sourcepointStore = useSourcepointStore()
  const bxWindow = (typeof window !== 'undefined' ? window : undefined) as unknown as BxMbWindow

  const clientConfig = ref<ClientConfig>({
    vw: 0,
    vh: 0,
    screenType: 'mobile',
    startOffsetTop: 0,
    slotDistanceOffset: 0,
    slotList: [],
    article: {},
    exclusionList: [],
    latestAllocatedDynamicAdSlots: [],
    latestRegisteredDynamicAdSlots: []
  })
  const oneTimeRegistrationAdSlots = ref<string[]>([])
  const latestAllocatedDynamicAdSlots = ref<string[]>([])
  const latestRegisteredDynamicAdSlots = ref<string[]>([])
  const emitedAllocationWithoutConsents = ref(false)

  const pageTypeLogic = computed<string>(() => {
    const meta = toValue(metaData)
    if (meta?.isVideoOnlyArticle) return 'videoOnlyArticle'
    if (adaStore.isAsyncContentMode) return 'asyncContent'
    if (pageStore.isArticleOrArticleVersion || meta?.behaveLikeArticle) return 'article'
    return 'structureterm'
  })

  const adSlotConfig = computed<DynamicAdSlots | undefined>(() => configStore?.rsConfig?.adSlotUsage?.dynamicAdSlots)

  const isArticleWithSidebar = computed(() => {
    return pageTypeLogic.value === 'article' && clientConfig.value.screenType === 'largeDesktop'
  })

  // When the article slot list contains 'ncb_top', the native crossbar top is pulled into the
  // article content (below the outstream slot) instead of being fixed below the article.
  // This is only the case on large desktop articles for brands that opt in via their config.
  const ncbInArticleModeActive = computed(() => {
    return pageTypeLogic.value === 'article' &&
      clientConfig.value.screenType === 'largeDesktop' &&
      clientConfig.value.slotList.includes('ncb_top')
  })

  // The fixed native crossbar top below the article is only rendered when we are not in
  // ncb-in-article mode, or as a fallback once the allocation decided that the article was
  // too short (or no marker was available) to place the crossbar inside the content.
  const showNcbTopBelowArticle = computed(() => {
    if (!ncbInArticleModeActive.value) return true
    return adaStore.dynamicAdSlotsAllocated && !adaStore.ncbTopPlacedInArticle
  })

  watch(() => sourcepointStore.consentsExist, (newVal) => {
    if (newVal && emitedAllocationWithoutConsents.value) {
      emitedAllocationWithoutConsents.value = false
      handleEventAllocation()
    }
  })

  onMounted(() => {
    EventBus.$on('ad-loader:allocation', handleEventAllocation)
  })

  function handleEventAllocation () {
    if (adaStore.isAsyncContentMode && !sourcepointStore.consentsExist) {
      emitedAllocationWithoutConsents.value = true
      return
    }
    nextTick(() => {
      dynamicAdSlotAllocation()
    })
  }

  function initAdaDynamicSlotLoader () {
    setClientConfig()
    // We want to initialize the Molten Bundle as quickly as possible,
    // so we initially only register the static (fixed positioned) ad slots.
    checkStaticSlotsToRegister()
    // and the dynamic slots will be registered afterwards
    initMoltenBundle()
    bxWindow.bxMb.clientConfig = clientConfig.value

    // while the handleEventAllocation method is called on the ad-loader:allocation event,
    // we also want to call it on the initial mount of the component,
    // if we are not in async content mode.
    // This is to ensure that the dynamic ad slots are allocated immediately
    if (!adaStore.isAsyncContentMode) {
      handleEventAllocation()
    }
  }

  /**
   * Setting up the configuration for rendering dynamic slots
   * based on the viewport dimensions, screen type, and other conditions.
   * The configuration is stored in the clientConfig object.
   */
  function setClientConfig () {
    if (typeof window === 'undefined') return

    const vw = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
    const vh = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
    const startOffsetTop = vh

    let screenType: ScreenType | undefined
    ;(Object.keys(screenTypeMinWidth) as ScreenType[]).forEach(el => {
      if (!screenType && vw >= screenTypeMinWidth[el]) {
        screenType = el
      }
    })
    const resolvedScreenType = screenType ?? 'mobile'

    const meta = toValue(metaData)
    const adSlotCfg = adSlotConfig.value
    if (!adSlotCfg?.orderedSlotLists) return
    let slotList: string[] = adSlotCfg.orderedSlotLists[pageTypeLogic.value][resolvedScreenType]
    const exclusionList: string[] = []

    if (meta?.disallowNativeTeasers || meta?.advertorial) {
      exclusionList.push('ncb_top')
    }

    if (meta?.disallowPosterAds || meta?.adaAutoNative) {
      exclusionList.push('outstream')
    }

    if (adaStore.staticMobile1OnHome) {
      exclusionList.push('mobile_1')
    }

    if (exclusionList.length) {
      slotList = slotList.filter(item => !exclusionList.includes(item))
    }

    clientConfig.value = {
      vw,
      vh,
      screenType: resolvedScreenType,
      startOffsetTop,
      slotDistanceOffset: adSlotCfg.slotDistanceOffset?.[resolvedScreenType]?.(clientConfig.value) ?? 0,
      article: {},
      slotList,
      exclusionList,
      latestAllocatedDynamicAdSlots: [],
      latestRegisteredDynamicAdSlots: []
    }

    if (resolvedScreenType === 'largeDesktop') {
      largeDesktopConfigMods(adSlotCfg)
    } else {
      // smallDesktop and mobile
      clientConfig.value.slotDistanceOffset = adSlotCfg.slotDistanceOffset?.[resolvedScreenType]?.(clientConfig.value) ?? 0
      clientConfig.value.startOffsetTop = adSlotCfg.startOffsetTop?.[resolvedScreenType]?.(clientConfig.value) ?? 0
    }
  }

  /**
   * These methods are called from setClientConfig based on the identified screen type
   * (large desktop, small desktop, or mobile).
   * They modify the clientConfig object's properties
   * based on the page type logic and viewport dimensions.
   */
  function largeDesktopConfigMods (adSlotCfg: DynamicAdSlots) {
    const cfg: DynamicAdClientConfig = clientConfig.value
    if (pageTypeLogic.value === 'article') {
      // in this case we just want to inject the outstream_div at the lower end of the article
      const asideWrapperNode = document.querySelector<HTMLElement>('.bx-aside-wrapper')
      clientConfig.value.article = {
        leadOffset: 0,
        asideWrapperHeight: asideWrapperNode?.clientHeight ?? 0
      }
      // lets check, if the lead paragraph is separated and placed above the sidebar
      const separateLeadParagraphNode = document.querySelector<HTMLElement>('main.asset-container > .bx-paragraph-list')
      // if there is a separated lead paragraph, we need to add its height to the offset
      if (separateLeadParagraphNode) {
        clientConfig.value.articleLeadOffset = separateLeadParagraphNode.clientHeight
      }
      // now we can calculate the correct startOffsetTop taking into account the lead offset and the aside wrapper height
      // asideWrapperNode.clientHeight * 0.75 + leadOffset
      clientConfig.value.startOffsetTop = adSlotCfg.startOffsetTopArticle?.largeDesktop?.(cfg as DynamicAdArticleClientConfig) ?? 0
    } else {
      // this.clientConfig.vh - 400
      clientConfig.value.startOffsetTop = adSlotCfg.startOffsetTop?.largeDesktop?.(cfg) ?? 0
    }
  }

  /**
   * Dynamically allocating ad slots based on the configured slotDistanceOffset and
   * the available slots in slotList. It iterates through a sorted list of marker offsets,
   * calculates the distance between each marker and the reference offset,
   * and assigns a slot from slotList to the marker if the distance is greater than
   * or equal to slotDistanceOffset and there are available slots.
   */
  function dynamicAdSlotAllocation () {
    latestAllocatedDynamicAdSlots.value = []
    latestRegisteredDynamicAdSlots.value = []
    // set initial referenceOffset to start calculating distances
    let referenceOffset = clientConfig.value.startOffsetTop!
    // fetch registered adSlotMarker
    const adSlotMarker = { ...adaStore.getAdSlotMarker }
    // object to save activated markers
    const activatedMarker: typeof adSlotMarker = {}
    // sort the adSlotMarker by keys (offset)
    const sortedMarker = Object.keys(adSlotMarker).map(Number).sort((a, b) => a - b)

    // loop through sorted markers
    sortedMarker.forEach((markerOffset) => {
      const marker = adSlotMarker[markerOffset] as typeof adSlotMarker[number] & {
        innerWidth: number,
        parentOffsetTop: number,
        renderSlot?: string | false,
        screenType?: ScreenType
      }
      const distance = markerOffset - referenceOffset
      const activatedMarkerCount = Object.keys(activatedMarker).length
      const wouldBeFirst = !activatedMarkerCount
      // define check helper by differ between distance for first and following markers but check if we have article and sidebar active
      const distanceLargeEnough = wouldBeFirst && !isArticleWithSidebar.value
        ? markerOffset > clientConfig.value.vh
        : distance >= clientConfig.value.slotDistanceOffset!
      // while we want to place superbanner within the content, we need to check, if the marker width is large enough
      const parentWidthLargeEnough = clientConfig.value.slotList[activatedMarkerCount]?.includes('superbanner') ? marker.innerWidth >= 800 : true
      // check if distance is large enough, the activatedMarkerCount is less than the available slots
      // and if aside rectangle placement is safe for above the fold before activate the marker
      if (
        distanceLargeEnough &&
        parentWidthLargeEnough &&
        activatedMarkerCount < clientConfig.value.slotList.length &&
        isSafePlacementForRectangleAside(marker.parentOffsetTop, clientConfig.value.slotList[activatedMarkerCount])
      ) {
        const slotToRender = clientConfig.value.slotList[activatedMarkerCount]
        marker.renderSlot = slotToRender
        marker.screenType = clientConfig.value.screenType
        activatedMarker[markerOffset] = marker
        referenceOffset = markerOffset
        // register dynamic slot, while it's not an outstream slot
        if (slotToRender !== 'outstream') {
          const pushSlot = slotToRender === 'ncb_top'
            ? ['teaser_11', 'teaser_12', 'teaser_13']
            : [slotToRender]
          latestAllocatedDynamicAdSlots.value.push(...pushSlot)
        }
      }
    })

    // propagate the activated markers to the store for initialization of the ad slots
    adaStore.setActivatedAdSlotMarker(activatedMarker)
    adaStore.setDynamicAdSlotsAllocated(true)

    // in ncb-in-article mode we placed the native crossbar top within the content only if a
    // suitable marker was far enough below the outstream slot. If it could not be placed, we
    // fall back to the fixed crossbar below the article and register its teasers here.
    if (ncbInArticleModeActive.value) {
      const ncbPlacedInArticle = latestAllocatedDynamicAdSlots.value.includes('teaser_11')
      adaStore.setNcbTopPlacedInArticle(ncbPlacedInArticle)
      if (!ncbPlacedInArticle) {
        latestAllocatedDynamicAdSlots.value.push('teaser_11', 'teaser_12', 'teaser_13')
      }
    }

    if (bxWindow.bxMb?.clientConfig) {
      bxWindow.bxMb.clientConfig.latestAllocatedDynamicAdSlots = latestAllocatedDynamicAdSlots.value
    }

    nextTick(() => {
      registerAdSlotOnce(latestAllocatedDynamicAdSlots.value)

      if (bxWindow.bxMb?.clientConfig) {
        bxWindow.bxMb.clientConfig.latestRegisteredDynamicAdSlots = latestRegisteredDynamicAdSlots.value
      }

      // set the async content ready for reload flag
      adaStore.setAsyncContentReadyForAdReload(true)
    })
  }

  function isSafePlacementForRectangleAside (parentOffsetTop: number, slotToRender: string) {
    // if we are on large Desktop and the slot to render is a rectangle, it's not safe to place it above the fold.
    // While the parentOffsetTop is smaller than the viewport height, we would generate a layout shift
    return !(clientConfig.value.screenType === 'largeDesktop' && slotToRender.includes('rectangle') && parentOffsetTop <= clientConfig.value.vh!)
  }

  /**
   * Registering static ad slots based on the viewport width and the metaData configuration.
   */
  function checkStaticSlotsToRegister () {
    const meta = toValue(metaData)

    if (clientConfig.value.vw! >= 768) {
      registerSlots.value.push('skyscraper_1')
    } else {
      registerSlots.value.push('mobile_10')
      // static mobile 1 is only placed on home otherwise dynamic
      if (adaStore.staticMobile1OnHome || meta?.pagestripMainId || pageTypeLogic.value === 'videoOnlyArticle') {
        registerSlots.value.push('mobile_1')
      }

      if (toValue(taboolaEnabled)) {
        registerSlots.value.push('tbmobile_1')
      }
    }

    if (clientConfig.value.vw! >= 888) {
      if (pageTypeLogic.value === 'videoOnlyArticle') {
        registerSlots.value.push('superbanner_1')
      } else {
        registerSlots.value.push('wallpaper_1', 'superbanner_1')
      }

      if (toValue(taboolaEnabled)) {
        registerSlots.value.push('tbsuperbanner_1')
      }
    }

    if (clientConfig.value.vw! >= 990) {
      registerSlots.value.push('dmofooter_1')
    }

    if (clientConfig.value.vw! >= 1440) {
      registerSlots.value.push('skyscraper_2')
    }

    if (pageTypeLogic.value === 'videoOnlyArticle') {
      registerSlots.value.push('outstream_div')
    }

    if (!meta?.disallowNativeTeasers && !meta?.advertorial && pageTypeLogic.value !== 'videoOnlyArticle') {
      // register teaser slots with fix position at once.
      // ---------------------
      // On article pages, the first three slots (teaser_11 to 13) have a fixed position below the article paragraphs and can be registered here initially.
      // On structureterm pages, the first three slots (teaser_11 to 13) are positioned dynamic, within the page content and will be registered separately through registerAdSlotOnce.
      // The computed value of enableNcbInDynamicMode is based on the pageTypeLogic and the dynamicAdSlotsEnabled configuration and used to determine the slots to register.
      // So, only if we are in enableNcbInDynamicMode, we register the first three teaser slots at this point.
      // In ncb-in-article mode the top teasers (11-13) are registered dynamically once the allocation
      // decides whether the crossbar goes inside the article or falls back below it, so we skip them here.
      const startingIndex = toValue(enableNcbInDynamicMode) && !ncbInArticleModeActive.value ? 1 : 4
      // 11 to 13 are for AdaNativeCrossbar top (3)
      // 14 to 19 are for AdaNativeCrossbar bottom (6)
      for (let i = startingIndex; i <= 9; i++) {
        registerSlots.value.push(`teaser_${10 + i}`)
      }
    }
  }

  /**
   * Register the ad slot for the molten bundle only once
   */
  function registerAdSlotOnce (adSlots: string[]) {
    if (!adaStore.blockAds && adSlots) {
      // check if molten bundle is already loaded, if not wait for it
      if (bxWindow.MoltenBundle?.cmd) {
        adSlots.forEach((adSlot) => {
          if (!oneTimeRegistrationAdSlots.value.includes(adSlot)) {
            oneTimeRegistrationAdSlots.value.push(adSlot)
            latestRegisteredDynamicAdSlots.value.push(adSlot)
            bxWindow.MoltenBundle.cmd.push(() => {
              bxWindow.MoltenBundle.push(adSlot)
            })
            // we need to add the new registered Slot to the adSlots list, so that we know it exists and we can recognize it for ad reloading
            bxWindow.bxMb.adSlots.push(adSlot)
          }
        })
      } else {
        setTimeout(() => {
          registerAdSlotOnce(adSlots)
        }, 100)
      }
    }
  }

  return {
    clientConfig,
    oneTimeRegistrationAdSlots,
    latestAllocatedDynamicAdSlots,
    latestRegisteredDynamicAdSlots,
    emitedAllocationWithoutConsents,
    pageTypeLogic,
    adSlotConfig,
    isArticleWithSidebar,
    ncbInArticleModeActive,
    showNcbTopBelowArticle,
    initAdaDynamicSlotLoader,
    handleEventAllocation,
    setClientConfig,
    dynamicAdSlotAllocation,
    isSafePlacementForRectangleAside,
    checkStaticSlotsToRegister,
    registerAdSlotOnce
  }
}
